import { ref } from "vue";

interface PlayerControls {
   playBtn: HTMLElement | null;
   pauseBtn: HTMLElement | null;
   muteBtn: HTMLElement | null;
   soundBtn: HTMLElement | null;
   audioTime: HTMLElement | null;
}

export function useAudioPlayer() {
   const player = ref<HTMLAudioElement | null>(null);
   const playing = ref<boolean>(false);
   const muted = ref<boolean>(false);
   const songCurrentTime = ref<string>("0:00");
   const songDuration = ref<string>("0:00");
   const songProgress = ref<number>(0);
   const playlistItem = ref<NodeListOf<HTMLElement> | HTMLElement[]>([]);
   let html: HTMLElement | null = null;
   let progress: HTMLElement | null = null;
   let buffer: HTMLElement | null = null;

   function initPlayer(url: string, id: string): void {
      if (!id) return;

      html = document.getElementById(id);
      player.value = new Audio(url);

      if (!html) return;

      const playBtn = html.querySelector<HTMLElement>(".control-play");
      const pauseBtn = html.querySelector<HTMLElement>(".control-pause");
      const muteBtn = html.querySelector<HTMLElement>(".control-mute");
      const soundBtn = html.querySelector<HTMLElement>(".control-sound");
      const audioTime = html.querySelector<HTMLElement>(".audio-time");
      progress = html.querySelector<HTMLElement>(".audio-progress-bar");
      buffer = html.querySelector<HTMLElement>(".audio-progress-buffer");
      playlistItem.value = html.querySelectorAll<HTMLElement>(
         ".playlist-audio-item",
      );

      setupPlayerAttr();
      bindEvents({ playBtn, pauseBtn, muteBtn, soundBtn, audioTime });
   }

   function bindEvents({
      playBtn,
      pauseBtn,
      muteBtn,
      soundBtn,
   }: PlayerControls): void {
      if (!player.value) return;
      player.value.addEventListener("loadedmetadata", setData);
      player.value.addEventListener("timeupdate", onTimeUpdate);
      player.value.addEventListener("ended", looper, false);

      progress?.addEventListener("click", seekClicked);

      playBtn?.addEventListener("click", togglePlay);
      pauseBtn?.addEventListener("click", togglePlay);

      muteBtn?.addEventListener("click", toggleSound);
      soundBtn?.addEventListener("click", toggleSound);

      window.addEventListener("audio:loop", ((ev: CustomEvent) => {
         if (ev.detail?.id === html?.id) {
            setLoop();
         }
      }) as EventListener);

      (playlistItem.value as NodeListOf<HTMLElement>).forEach((el) => {
         el.addEventListener("click", playPlaylistSong);
      });
   }

   function playPlaylistSong(ev: Event): void {
      const target = ev.target as HTMLElement;
      const src = target.dataset.src ?? "";
      const title = target.innerText;

      if (playing.value) {
         togglePlay();
      }

      const titleEl = html?.querySelector<HTMLElement>(".audio-title strong");
      if (titleEl) titleEl.innerText = title;
      player.value?.setAttribute("src", src);
      player.value?.load();
      togglePlay();
   }

   function setupPlayerAttr(): void {
      const audio = html?.querySelector<HTMLAudioElement>("audio");
      if (!audio || !player.value) return;
      if (audio.loop) {
         player.value.loop = true;
      }
      if (audio.autoplay) {
         player.value.autoplay = true;
      }
   }

   function setData(): void {
      if (!player.value) return;
      const duration = player.value.duration;
      songDuration.value = convertTime(duration);
      progress?.setAttribute("max", String(duration));
      buffer?.setAttribute("max", String(duration));
      updateAudioTime();
   }

   function onTimeUpdate(): void {
      if (!player.value) return;
      songCurrentTime.value = convertTime(player.value.currentTime);
      songProgress.value = player.value.currentTime;
      progress?.setAttribute("value", String(songProgress.value));
      updateAudioTime();

      if (player.value.buffered.length) {
         const progressEnd = player.value.buffered.end(
            player.value.buffered.length - 1,
         );
         buffer?.setAttribute("value", String(progressEnd));
      }
   }

   function convertTime(time: number): string {
      const minutes = Math.floor(time / 60);
      const rawSeconds = Math.floor(time % 60);
      const seconds = rawSeconds < 10 ? `0${rawSeconds}` : `${rawSeconds}`;
      return `${minutes}:${seconds}`;
   }

   function seekClicked(ev: Event): void {
      if (!player.value || !progress) return;
      const mouseEvent = ev as MouseEvent;
      const position = mouseEvent.pageX - progress.getBoundingClientRect().left;
      const delta = position / progress.offsetWidth;
      songProgress.value = player.value.duration * delta;
      player.value.currentTime = songProgress.value;
      progress.setAttribute("value", String(songProgress.value));
   }

   function togglePlay(): void {
      if (!player.value) return;
      if (playing.value) {
         player.value.pause();
      } else {
         player.value.play();
      }
      playing.value = !playing.value;
   }

   function toggleSound(): void {
      if (!player.value) return;
      player.value.muted = !muted.value;
      muted.value = !muted.value;
   }

   function looper(): void {
      if (!player.value) return;
      if (player.value.loop) {
         player.value.currentTime = 0;
      } else {
         playing.value = false;
         player.value.pause();
      }
   }

   function setLoop(): void {
      if (!player.value) return;
      player.value.loop = !player.value.loop;
      const audio = html?.querySelector<HTMLAudioElement>("audio");
      if (audio) audio.loop = player.value.loop;
   }

   function updateAudioTime(): void {
      const audioTime = html?.querySelector<HTMLElement>(".audio-time");
      if (audioTime) {
         audioTime.innerHTML = `${songCurrentTime.value}/${songDuration.value}`;
      }
   }

   return {
      initPlayer,
      playing,
      muted,
   };
}
